import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Image, Loader2, Trash2, Plus, Sparkles, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [memes, setMemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchMemes = async () => {
      try {
        const response = await axios.get(`${API}/memes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMemes(response.data);
      } catch {
        toast.error('Failed to load your memes');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      fetchMemes();
    }
  }, [isAuthenticated, authLoading, token, navigate]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/memes/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemes(prev => prev.filter(m => m.id !== deleteTarget));
      toast.success('Meme deleted');
    } catch (error) {
      toast.error('Failed to delete meme');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#bef264]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-[#3f3f46] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Sign In Required</h2>
          <p className="text-[#a1a1aa] mb-6">Please sign in to view your saved memes</p>
          <Link to="/">
            <Button className="bg-[#bef264] text-black hover:bg-[#a3e635]">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#09090b] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="text-4xl font-bold text-white font-['Space_Grotesk']">
              My Gallery
            </h1>
            <p className="text-[#a1a1aa] mt-1">
              {memes.length} saved meme{memes.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link to="/editor">
            <Button className="bg-[#bef264] text-black hover:bg-[#a3e635] font-medium">
              <Plus className="w-4 h-4 mr-2" />
              Create New Meme
            </Button>
          </Link>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-[#bef264]" />
          </div>
        ) : memes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {memes.map((meme, index) => (
              <div
                key={meme.id}
                className="group relative aspect-square bg-[#18181b] rounded-xl overflow-hidden border border-[#27272a] animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`meme-card-${meme.id}`}
              >
                <img
                  src={meme.image_url}
                  alt="Saved meme"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <Button
                    onClick={() => setDeleteTarget(meme.id)}
                    variant="outline"
                    size="icon"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                    data-testid={`delete-meme-${meme.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Date badge */}
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="text-xs text-white/70 bg-black/50 px-2 py-1 rounded">
                    {new Date(meme.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-20">
            <Image className="w-16 h-16 text-[#3f3f46] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No memes yet</h3>
            <p className="text-[#a1a1aa] mb-6">Start creating and save your favorite memes!</p>
            <Link to="/editor">
              <Button className="bg-[#bef264] text-black hover:bg-[#a3e635]">
                <Sparkles className="w-4 h-4 mr-2" />
                Create Your First Meme
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#18181b] border-[#27272a]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Meme?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              This action cannot be undone. This meme will be permanently deleted from your gallery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272a] text-white hover:bg-[#27272a]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GalleryPage;
